import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useState } from 'react';
import {
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

// Context and Utils
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';

interface Task {
  id: number;
  title: string;
  completed: boolean;
  description: string;
  priority: 'High' | 'Medium' | 'Low';
  estimatedHours: number;
}

interface UserStoryDetail {
  id: number;
  title: string;
  status: 'TODO' | 'IN_PROGRESS' | 'TO_REVIEW' | 'DONE';
  shortDescription: string;
  longDescription: string;
  assignedTo: {
    name: string;
    avatar: string;
  };
  assignedBy: {
    name: string;
    avatar: string;
  };
  priority: 'High' | 'Medium' | 'Low';
  storyPoints: number;
  createdDate: string;
  dueDate: string;
}

export default function UserStoryDetailScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  
  const storyId = route.params?.storyId || 1;
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());

  const [tasks, setTasks] = useState<Task[]>([
    {
      id: 1,
      title: 'Design Azure authentication flow',
      completed: true,
      description: `Create a comprehensive Azure AD authentication system:

• **Azure AD Integration**: Configure app registration and permissions
• **Token Management**: Implement secure token handling and refresh logic
• **User Roles**: Define role-based access control (RBAC)
• **Security**: Implement proper security headers and CORS policies
• **Error Handling**: Graceful error handling for authentication failures

**Technical Requirements:**
- Use Azure AD B2C for user authentication
- Implement JWT token validation
- Add multi-factor authentication support
- Ensure GDPR compliance for user data`,
      priority: 'High',
      estimatedHours: 12
    },
    {
      id: 2,
      title: 'Implement container orchestration',
      completed: false,
      description: `Deploy and manage containerized applications:

**Azure Container Instances:**
• Set up container registry and image management
• Configure auto-scaling based on demand
• Implement health checks and monitoring
• Set up networking and security groups

**Kubernetes Integration:**
• Deploy to Azure Kubernetes Service (AKS)
• Configure ingress controllers and load balancers
• Implement service mesh for microservices
• Set up monitoring with Azure Monitor`,
      priority: 'High',
      estimatedHours: 16
    },
    {
      id: 3,
      title: 'Add data analytics dashboard',
      completed: false,
      description: `Create real-time analytics and reporting:

• **Data Visualization**: Interactive charts and graphs
• **Real-time Metrics**: Live performance monitoring
• **Custom Reports**: User-defined report generation
• **Export Functionality**: PDF and Excel export options
• **Mobile Responsive**: Optimized for all device sizes

**Data Sources:**
- Azure Application Insights
- Azure Monitor metrics
- Custom application logs
- User interaction data`,
      priority: 'Medium',
      estimatedHours: 20
    },
    {
      id: 4,
      title: 'Write comprehensive tests',
      completed: false,
      description: `Ensure code quality and reliability:

**Test Categories:**
• Unit tests for all business logic
• Integration tests for API endpoints
• End-to-end tests for user workflows
• Performance and load testing
• Security vulnerability testing

**Testing Tools:**
- Jest for unit testing
- Cypress for E2E testing
- Azure DevOps for CI/CD
- SonarQube for code quality`,
      priority: 'Low',
      estimatedHours: 24
    }
  ]);

  // Mock story data - in real app this would come from props or API
  const story: UserStoryDetail = {
    id: storyId,
    title: 'Azure Cloud Infrastructure Setup',
    status: 'IN_PROGRESS',
    shortDescription: 'Implement comprehensive Azure cloud infrastructure for the learning platform',
    longDescription: `This user story covers the complete implementation of Azure cloud infrastructure for our learning platform. The infrastructure should be scalable, secure, and cost-effective while supporting our growing user base.

**Acceptance Criteria:**
1. Azure AD authentication system is fully functional
2. Container orchestration supports auto-scaling
3. Data analytics provide real-time insights
4. All components are properly tested and documented
5. Infrastructure follows Azure best practices
6. Cost optimization measures are implemented

**Business Value:**
This infrastructure enables our platform to scale globally while maintaining performance and security. It provides the foundation for future feature development and supports our mission to democratize cloud education.

**Technical Considerations:**
- Implement Infrastructure as Code using Terraform
- Ensure high availability across multiple regions
- Set up comprehensive monitoring and alerting
- Design for disaster recovery and backup strategies
- Optimize costs using Azure Reserved Instances`,
    assignedTo: { name: 'Dr. Sarah Chen', avatar: 'SC' },
    assignedBy: { name: 'Prof. Ahmed Hassan', avatar: 'AH' },
    priority: 'High',
    storyPoints: 21,
    createdDate: '2024-01-15',
    dueDate: '2024-03-01'
  };

  const toggleTask = (taskId: number) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, completed: !task.completed }
        : task
    ));
  };

  const toggleTaskExpansion = (taskId: number) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return colors.warning;
      case 'IN_PROGRESS': return colors.primary;
      case 'TO_REVIEW': return colors.info;
      case 'DONE': return colors.success;
      default: return colors.textSecondary;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'TODO': return 'To Do';
      case 'IN_PROGRESS': return 'In Progress';
      case 'TO_REVIEW': return 'To Review';
      case 'DONE': return 'Done';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return colors.error;
      case 'Medium': return colors.warning;
      case 'Low': return colors.success;
      default: return colors.textSecondary;
    }
  };

  const completedTasks = tasks.filter(task => task.completed).length;
  const totalTasks = tasks.length;

  const renderTask = (task: Task) => {
    const isExpanded = expandedTasks.has(task.id);
    
    return (
      <View
        key={task.id}
        style={[
          styles.taskCard,
          { 
            backgroundColor: colors.cardBackground,
            borderLeftColor: getPriorityColor(task.priority),
            borderLeftWidth: 4,
          }
        ]}
      >
        <View style={styles.taskHeader}>
          <TouchableOpacity
            style={styles.checkbox}
            onPress={() => toggleTask(task.id)}
          >
            <Ionicons 
              name={task.completed ? "checkmark-circle" : "ellipse-outline"} 
              size={20} 
              color={task.completed ? colors.success : colors.textSecondary} 
            />
          </TouchableOpacity>
          
          <View style={styles.taskInfo}>
            <View style={styles.taskTitleRow}>
              <Text style={[
                styles.taskTitle,
                { color: colors.textPrimary },
                task.completed && styles.completedTask
              ]}>
                {task.title}
              </Text>
              <View style={styles.taskMeta}>
                <View style={[
                  styles.priorityBadge,
                  { backgroundColor: getPriorityColor(task.priority) }
                ]}>
                  <Text style={[styles.priorityText, { color: colors.badgeText }]}>
                    {task.priority}
                  </Text>
                </View>
                <Text style={[styles.estimatedHours, { color: colors.textSecondary }]}>
                  {task.estimatedHours}h
                </Text>
              </View>
            </View>
            
            <TouchableOpacity
              style={styles.expandButton}
              onPress={() => toggleTaskExpansion(task.id)}
            >
              <Text style={[styles.expandText, { color: colors.textSecondary }]}>
                {isExpanded ? 'Hide details' : 'Show details'}
              </Text>
              <Ionicons 
                name={isExpanded ? "chevron-up" : "chevron-down"} 
                size={16} 
                color={colors.textSecondary} 
              />
            </TouchableOpacity>
          </View>
        </View>

        {isExpanded && (
          <View style={[styles.taskDescription, { backgroundColor: colors.surfaceSecondary }]}>
            <Text style={[styles.descriptionText, { color: colors.textSecondary }]}>
              {task.description}
            </Text>
          </View>
        )}
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]} numberOfLines={1}>
              {story.title}
            </Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              Story #{story.id}
            </Text>
          </View>
          <View style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(story.status) }
          ]}>
            <Text style={[styles.statusText, { color: colors.badgeText }]}>
              {getStatusLabel(story.status)}
            </Text>
          </View>
        </View>
      </View>

      {/* Scrollable Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.contentPadding}>
          {/* Story Info */}
          <View style={[styles.infoCard, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.infoHeader}>
              <View style={[
                styles.priorityBadge,
                { backgroundColor: getPriorityColor(story.priority) }
              ]}>
                <Text style={[styles.priorityText, { color: colors.badgeText }]}>
                  {story.priority} Priority
                </Text>
              </View>
              <View style={[styles.pointsBadge, { backgroundColor: colors.surfaceSecondary }]}>
                <Text style={[styles.pointsText, { color: colors.textSecondary }]}>
                  {story.storyPoints} Story Points
                </Text>
              </View>
            </View>

            <View style={styles.infoContent}>
              <View style={styles.descriptionSection}>
                <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
                  Short Description
                </Text>
                <Text style={[styles.descriptionText, { color: colors.textSecondary }]}>
                  {story.shortDescription}
                </Text>
              </View>

              <View style={styles.dateInfo}>
                <View style={styles.dateItem}>
                  <Ionicons name="calendar" size={12} color={colors.textSecondary} />
                  <Text style={[styles.dateText, { color: colors.textSecondary }]}>
                    Created: {story.createdDate}
                  </Text>
                </View>
                <View style={styles.dateItem}>
                  <Ionicons name="time" size={12} color={colors.textSecondary} />
                  <Text style={[styles.dateText, { color: colors.textSecondary }]}>
                    Due: {story.dueDate}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Assignment Info */}
          <View style={[styles.infoCard, { backgroundColor: colors.cardBackground }]}>
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
              Assignment
            </Text>
            <View style={styles.assignmentInfo}>
              <View style={styles.assigneeItem}>
                <Ionicons name="person-check" size={16} color={colors.textSecondary} />
                <View style={[styles.avatar, { backgroundColor: colors.surfaceSecondary }]}>
                  <Text style={[styles.avatarText, { color: colors.textSecondary }]}>
                    {story.assignedBy.avatar}
                  </Text>
                </View>
                <View style={styles.assigneeDetails}>
                  <Text style={[styles.assigneeName, { color: colors.textPrimary }]}>
                    {story.assignedBy.name}
                  </Text>
                  <Text style={[styles.assigneeLabel, { color: colors.textSecondary }]}>
                    Assigned By
                  </Text>
                </View>
              </View>
              <View style={styles.assigneeItem}>
                <Ionicons name="person" size={16} color={colors.textSecondary} />
                <View style={[styles.avatar, { backgroundColor: colors.primary }]}>
                  <Text style={[styles.avatarText, { color: colors.badgeText }]}>
                    {story.assignedTo.avatar}
                  </Text>
                </View>
                <View style={styles.assigneeDetails}>
                  <Text style={[styles.assigneeName, { color: colors.textPrimary }]}>
                    {story.assignedTo.name}
                  </Text>
                  <Text style={[styles.assigneeLabel, { color: colors.textSecondary }]}>
                    Assigned To
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Long Description */}
          <View style={[styles.infoCard, { backgroundColor: colors.cardBackground }]}>
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
              Detailed Description
            </Text>
            <Text style={[styles.descriptionText, { color: colors.textSecondary }]}>
              {story.longDescription}
            </Text>
          </View>

          {/* Tasks */}
          <View style={[styles.infoCard, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.tasksHeader}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
                Tasks ({completedTasks}/{totalTasks})
              </Text>
              <Text style={[styles.completionText, { color: colors.textSecondary }]}>
                {Math.round((completedTasks / totalTasks) * 100)}% Complete
              </Text>
            </View>

            <View style={styles.tasksList}>
              {tasks.map(renderTask)}
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
  },
  contentPadding: {
    padding: 16,
  },
  infoCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  infoHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  pointsBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  pointsText: {
    fontSize: 10,
    fontWeight: '500',
  },
  infoContent: {
    gap: 12,
  },
  descriptionSection: {
    gap: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 20,
  },
  dateInfo: {
    flexDirection: 'row',
    gap: 16,
  },
  dateItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  dateText: {
    fontSize: 12,
  },
  assignmentInfo: {
    gap: 12,
  },
  assigneeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  assigneeDetails: {
    flex: 1,
  },
  assigneeName: {
    fontSize: 14,
    fontWeight: '500',
  },
  assigneeLabel: {
    fontSize: 12,
  },
  tasksHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  completionText: {
    fontSize: 12,
  },
  tasksList: {
    gap: 8,
  },
  taskCard: {
    borderRadius: 8,
    // Liseré d'accent gauche RECTILIGNE (coins gauches carrés, cf. squareLeftCorners).
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    padding: 12,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  taskHeader: {
    flexDirection: 'row',
    gap: 12,
  },
  checkbox: {
    padding: 2,
  },
  taskInfo: {
    flex: 1,
  },
  taskTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  taskTitle: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
    marginRight: 8,
  },
  completedTask: {
    textDecorationLine: 'line-through',
    opacity: 0.6,
  },
  taskMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  estimatedHours: {
    fontSize: 12,
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  expandText: {
    fontSize: 12,
  },
  taskDescription: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
  },
});
